import { GetStaticPaths, GetStaticProps } from 'next';

import { Container, Heading } from '@chakra-ui/react';

import { TypeArea, TypeAreaSkeleton, TypeCity, TypeCitySkeleton, TypeJobAdvertSkeleton } from 'generated/contentful';

import { ParsedUrlQuery } from 'querystring';

import { RECRUIT_PATH_PREFIX } from 'common/constants';

import { createClient } from 'common/lib/contentful';

import Layout from 'common/components/Layout/Layout';
import Paginator from 'common/components/Paginator/Paginator';

import JobsBoard from 'features/jobs-board/components/JobsBoard';
import { JobsBoardAdvertProps } from 'features/jobs-board/components/JobsBoardAdvert';

export interface CityJobsBoardParams extends ParsedUrlQuery {
  city: string;
  page?: string;
}

export interface CityJobsBoardProps {
  city: TypeCity<'WITHOUT_UNRESOLVABLE_LINKS', string>;
  cities: TypeCity<'WITHOUT_UNRESOLVABLE_LINKS', string>[];
  areas: TypeArea<'WITHOUT_UNRESOLVABLE_LINKS', string>[];
  currentPage: number;
  maxPage: number;
  items: JobsBoardAdvertProps[];
}

export const PAGE_LIMIT = 10;

export const getStaticPaths: GetStaticPaths<CityJobsBoardParams> = async () => {
  const client = createClient();

  const cities = await client.getEntries<TypeCitySkeleton>({
    content_type: 'city',
    limit: 1000
  });

  const paths = cities.items.map(city => ({
    params: {
      city: city.fields.slug
    }
  }));

  return {
    paths,
    fallback: false
  };
};

export const getStaticProps: GetStaticProps<CityJobsBoardProps> = async context => {
  const client = createClient();

  const { city: citySlug, page: pageSlug } = context.params as CityJobsBoardParams;

  const city = await client.withoutUnresolvableLinks.getEntries<TypeCitySkeleton>({
    content_type: 'city',
    'fields.slug[in]': [citySlug]
  });

  const cities = await client.withoutUnresolvableLinks.getEntries<TypeCitySkeleton>({
    content_type: 'city',
    limit: 100,
    include: 2,
    order: ['fields.name']
  });

  const areas = await client.withoutUnresolvableLinks.getEntries<TypeAreaSkeleton>({
    content_type: 'area',
    limit: 1000,
    include: 2,
    order: ['fields.name']
  });

  const jobAdverts = await client.withoutUnresolvableLinks.getEntries<TypeJobAdvertSkeleton>({
    content_type: 'jobAdvert',
    limit: 100,
    order: ['fields.slug']
  });

  // Filter areas for this city.
  const cityAreas = areas.items.filter(area => {
    return area.fields.city?.fields.slug === citySlug;
  });

  // Job adverts by area.
  const items: JobsBoardAdvertProps[] = cityAreas.flatMap(area =>
    jobAdverts.items.map(jobAdvert => ({ jobAdvert, area }))
  );

  // Job adverts by city.
  items.unshift(
    ...jobAdverts.items.map(jobAdvert => ({
      jobAdvert,
      city: city.items[0]
    }))
  );

  const currentPage = pageSlug ? parseInt(pageSlug, 10) : 1;
  const maxPage = Math.ceil(items.length / PAGE_LIMIT);
  const startIndex = PAGE_LIMIT * (currentPage - 1);
  const endIndex = startIndex + PAGE_LIMIT;

  return {
    props: {
      city: city.items[0] as TypeCity<'WITHOUT_UNRESOLVABLE_LINKS', string>,
      cities: cities.items as TypeCity<'WITHOUT_UNRESOLVABLE_LINKS', string>[],
      areas: areas.items as TypeArea<'WITHOUT_UNRESOLVABLE_LINKS', string>[],
      maxPage,
      currentPage,
      items: items.slice(startIndex, endIndex)
    }
  };
};

export default function CityJobsBoard({ city, cities, areas, currentPage, maxPage, items }: CityJobsBoardProps) {
  return (
    <Layout
      pageTitle={`Search Cleaning Jobs in ${city.fields.name}`}
      description={`We have cleaner jobs available in all areas of ${city.fields.name}, so if you're looking for full-time or part-time cleaning work, search our jobs now!`}
      showStatusBar={false}
      nav="recruit"
    >
      <Container maxW="container.md" py={[12, null, 16]}>
        <Heading size="2xl" mb={4}>
          Search Cleaning Jobs
        </Heading>

        <JobsBoard city={city} cities={cities} areas={areas} items={items} mb={10} />

        <Paginator
          currentPage={currentPage}
          maxPage={maxPage}
          url={`${RECRUIT_PATH_PREFIX}/jobs/${city.fields.slug}/`}
        />
      </Container>
    </Layout>
  );
}
